import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, AlertTriangle, CheckCircle, TrendingUp, Shield } from 'lucide-react';
import { StatusDot } from '@/components/shared/StatusDot';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MspDashboardPage() {
  const token = localStorage.getItem('nexus_token');
  const { data: overview, isLoading } = useQuery({
    queryKey: ['msp-overview'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/msp/overview`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
    refetchInterval: 30000,
  });

  const healthy = (overview || []).filter(t => t.health === 'healthy').length;
  const warning = (overview || []).filter(t => t.health === 'warning').length;
  const critical = (overview || []).filter(t => t.health === 'critical').length;

  return (
    <div data-testid="msp-dashboard" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)]">MSP Overview</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Multi-client dashboard • {(overview || []).length} clients</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="nexus-card p-3"><p className="text-[9px] text-[var(--nexus-text-muted)] uppercase">Healthy</p><p className="font-heading text-2xl font-bold text-[var(--nexus-success)]">{healthy}</p></div>
        <div className="nexus-card p-3"><p className="text-[9px] text-[var(--nexus-text-muted)] uppercase">Warning</p><p className="font-heading text-2xl font-bold text-[var(--nexus-warning)]">{warning}</p></div>
        <div className="nexus-card p-3"><p className="text-[9px] text-[var(--nexus-text-muted)] uppercase">Critical</p><p className="font-heading text-2xl font-bold text-[var(--nexus-danger)]">{critical}</p></div>
      </div>

      <div className="nexus-card relative overflow-hidden">
        <ScrollArea className="h-[500px]">
          <table className="w-full">
            <thead className="sticky top-0 bg-[var(--nexus-bg-elevated)]">
              <tr className="border-b border-[var(--nexus-border)]">
                <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-4 py-2.5">Client</th>
                <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-4 py-2.5">Health</th>
                <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-right px-4 py-2.5">Open</th>
                <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-right px-4 py-2.5">P1/P2</th>
                <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-right px-4 py-2.5">Resolved</th>
                <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-right px-4 py-2.5">Auto Rate</th>
                <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-4 py-2.5">Plan</th>
              </tr>
            </thead>
            <tbody>
              {(overview || []).map(client => (
                <tr key={client.tenant_id} className="border-b border-[var(--nexus-border)] hover:bg-white/[0.02]">
                  <td className="px-4 py-2.5"><span className="text-xs font-semibold text-[var(--nexus-text-primary)]">{client.name}</span><br/><span className="font-mono text-[9px] text-[var(--nexus-text-muted)]">{client.domain}</span></td>
                  <td className="px-4 py-2.5"><StatusDot status={client.health === 'healthy' ? 'active' : client.health === 'warning' ? 'warning' : 'danger'} pulse={client.health !== 'healthy'} size="md" /></td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-[var(--nexus-text-primary)]">{client.open_tickets}</td>
                  <td className="px-4 py-2.5 text-right"><span className={`font-mono text-xs font-bold ${client.active_p1_p2 > 0 ? 'text-[var(--nexus-danger)]' : 'text-[var(--nexus-text-muted)]'}`}>{client.active_p1_p2}</span></td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-[var(--nexus-success)]">{client.resolved}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-[var(--nexus-accent)]">{client.auto_resolution_rate}%</td>
                  <td className="px-4 py-2.5"><span className="font-mono text-[10px] px-2 py-0.5 rounded bg-[#0d2847] text-[var(--nexus-primary-bright)] border border-blue-500/30">{client.plan}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </div>
    </div>
  );
}
